import { useEffect, useRef, useState } from 'react';
import {
  ApiError,
  META_SAVE_ERRORS,
  fetchMetaAdAccounts,
  fetchMetaInstagramAccounts,
  fetchMetaPages,
  fetchMetaPixels,
  grantMetaInstagram,
  grantMetaPages,
  saveMetaSelections,
  type MetaAdAccount,
  type MetaInstagramAccountsResponse,
  type MetaPage,
} from '../../api/client';
import {
  EMPTY_PAGES_STEP,
  clearPageGrantDraft,
  grantPromptText,
  grantReturnNotice,
  isSessionTerminalStatus,
  pagesDroppedByReplace,
  pagesNextEnabled,
  pagesStepFromResponse,
  readPageGrantDraft,
  togglePage,
  toggleAllPages,
  writePageGrantDraft,
  type MetaGrantReturn,
  type PagesOutcome,
  type PagesStepState,
} from './metaPages';
import {
  EMPTY_INSTAGRAM_STEP,
  INSTAGRAM_GRANT_BUTTON_LABEL,
  INSTAGRAM_LIST_FOOTNOTES,
  INSTAGRAM_LOADING_TEXT,
  buildMetaSavePayloadWithInstagram,
  clearInstagramGrantDraft,
  instagramAccountSubtitle,
  instagramAccountTitle,
  instagramDroppedByReplace,
  instagramEmptyState,
  instagramFooterHint,
  instagramGrantPromptText,
  instagramGrantReturnNotice,
  instagramIncompleteNotice,
  instagramLinkedLeftAsIsText,
  instagramNextEnabled,
  instagramSelectionCount,
  instagramStepFromResponse,
  instagramUnconfirmedNotice,
  readInstagramGrantDraft,
  restorePagesAnswer,
  toggleAllInstagramAccounts,
  toggleInstagramAccount,
  writeInstagramGrantDraft,
  type InstagramOutcome,
  type InstagramResumedSelection,
  type InstagramStepState,
} from './metaInstagram';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { Loader2, Search, X } from 'lucide-react';

interface MetaAccountSelectorProps {
  sessionId: string;
  onSaved: (count: number) => void;
  onClose: () => void;
  /** Result of the last on-demand grant — the Page grant (VIBE-1916) or the
   *  Instagram grant (VIBE-1990) — including the case where the user switched the
   *  ADS permission off on the re-consent screen and the server discarded the
   *  whole grant. Used only to phrase the message — `GET /pages` and `GET
   *  /instagram-accounts` are the authoritative answers, and the steps render
   *  from those. Which grant it describes is decided by the draft this wizard
   *  parked, never by this value. */
  grantReturn?: MetaGrantReturn;
}

/** Pixel flattened across the selected ad accounts (mvp's shape). */
interface FlatPixel {
  id: string;
  name: string;
  adAccountId: string;
}

/** Named, because the step numbers are load-bearing in a dozen places and one
 *  inserted step (VIBE-1990) had to move every literal at once. */
const STEP_ACCOUNTS = 1;
const STEP_PAGES = 2;
const STEP_INSTAGRAM = 3;
const STEP_PIXELS = 4;
const STEPS = [STEP_ACCOUNTS, STEP_PAGES, STEP_INSTAGRAM, STEP_PIXELS];

// The Instagram title equals the grant button's label (owner decision 2026-09-28, as in the mvp wizard).
const STEP_TITLES = ['Select Ad Accounts', 'Select Pages', 'Connect Instagram', 'Select Pixels'];

/** Where the Instagram listing is. `idle` never renders — every way onto the
 *  step starts the listing — and is drawn like `loading` if it ever does. */
type InstagramListStatus = 'idle' | 'loading' | 'loaded' | 'failed';

/** The redirect's report, when it is about the OTHER grant. */
const NO_GRANT_RETURN: MetaGrantReturn = { kind: 'none' };

const pixelKey = (p: FlatPixel) => `${p.adAccountId}:${p.id}`;

/** Order-independent, so a resumed prefetch and a later one agree on "already fetched". */
const pixelAccountsKey = (accountIds: readonly string[]) => [...accountIds].sort().join(',');

/** Meta may answer without a name; never render an empty row. */
const pageLabel = (p: MetaPage) => p.name ?? p.id;

/**
 * Meta connect wizard (mirrors mvp's MetaAccountSelector): four steps —
 * ad accounts → pages → Instagram accounts → pixels — with a progress bar,
 * a search on every step except Instagram, and Select All. Pixels are
 * prefetched in the background when leaving step 1 and the step is optional
 * (Skip connects without pixels).
 *
 * The Pages step is optional too (VIBE-1916). Two rules carry that, and both are
 * in `./metaPages`:
 *  - Skipping sends NO page key, so existing links are left exactly as found —
 *    they are shared with the mvp wizard, and `[]` would erase them.
 *  - Next is enabled only when the selection DIFFERS from the seeded baseline,
 *    never when "something is ticked". A count-based rule would read as "you may
 *    unlink any Pages except the last one" and make a deliberate clear impossible.
 *
 * The Instagram step (VIBE-1990) is the Pages step again, rule for rule, in
 * `./metaInstagram`: the same three-valued key, the same Next rule, the same
 * replace semantics and the same drop disclosure. It needs no Page — accounts
 * come from Business Manager assets, ad accounts, or (with the Page grant) Pages —
 * and it has its own grant (base + `instagram_basic`).
 */
export default function MetaAccountSelector({
  sessionId, onSaved, onClose, grantReturn = { kind: 'none' },
}: MetaAccountSelectorProps) {
  // What the last grant round trip through Meta parked. The wizard session
  // carries tokens, not selections, so without these the user comes back to an
  // empty step 1.
  //
  // 🔴 The Instagram draft WINS over the Page draft, and a Page draft is not even
  // read when there is one: the Instagram draft already carries the ad accounts
  // AND the Pages decision, so it is the later and the fuller of the two. Both
  // are cleared after seeding (effect below).
  const [igDraft] = useState(() => readInstagramGrantDraft(sessionId));
  const [pageDraft] = useState(() => (igDraft ? null : readPageGrantDraft(sessionId)));
  const resumeDraft = igDraft ?? pageDraft;
  // The redirect reports on the grant this wizard left for. Back from the
  // Instagram grant, an `ads_scope_revoked` is about THAT attempt, so the Pages
  // card must not re-tell it as a failed Page grant.
  const pagesGrantReturn = igDraft ? NO_GRANT_RETURN : grantReturn;
  // This mount is the return from the Instagram dialog — the only evidence of
  // that trip, since its redirect reports no outcome (`instagramGrantPromptText`).
  const resumedFromInstagramGrant = igDraft !== null;

  const [adAccounts, setAdAccounts] = useState<MetaAdAccount[]>([]);
  const [pagesStep, setPagesStep] = useState<PagesStepState>(EMPTY_PAGES_STEP);
  const [loading, setLoading] = useState(true);
  // 🔴 `error` is FATAL: the whole body and the entire footer render under
  // `{!loading && !error && …}`, so setting it removes the list, Skip, Back and
  // Next. That is right for a failed LOAD — there is nothing to act on. It is
  // wrong for a failed SAVE, where every control still works and the user is
  // being asked to change something: the old code blanked the dialog and left
  // only the close button, which drops the session and costs a full re-OAuth
  // plus a second Page grant. `saveError` is the same message without the gate.
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);

  const [currentStep, setCurrentStep] = useState(
    igDraft ? STEP_INSTAGRAM : pageDraft ? STEP_PAGES : STEP_ACCOUNTS,
  );
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>(
    resumeDraft?.adAccountIds ?? [],
  );
  const [selectedPixels, setSelectedPixels] = useState<FlatPixel[]>([]);
  const [availablePixels, setAvailablePixels] = useState<FlatPixel[]>([]);
  // `true` from the start on an Instagram resume: the resume effect starts the
  // prefetch, and it may not write state synchronously (see that effect).
  const [loadingPixels, setLoadingPixels] = useState(igDraft !== null);
  const [pixelFetchError, setPixelFetchError] = useState<string | null>(null);

  // Track which accounts we last fetched pixels for, to avoid redundant fetches
  const fetchedForAccountsRef = useRef<string>('');
  // Which pixel fetch is the current one. Changing the ad-account selection
  // mid-flight otherwise resolves last-by-timing rather than last-by-intent, and
  // the user gets the previous selection's pixels.
  const pixelGenerationRef = useRef(0);

  // Filter queries for each step's list (by name, case-insensitive). The
  // Instagram step has none.
  const [accountSearch, setAccountSearch] = useState('');
  const [pageSearch, setPageSearch] = useState('');
  const [pixelSearch, setPixelSearch] = useState('');

  const [saving, setSaving] = useState(false);
  const [granting, setGranting] = useState(false);
  const [grantError, setGrantError] = useState<string | null>(null);
  // What the last round trip through Meta did, when it needs saying. Non-fatal
  // by construction: the session is alive, Skip and Next both work, and the only
  // thing missing is an explanation. Cleared when the user tries again.
  const [grantNotice, setGrantNotice] = useState<string | null>(
    // Phrased for the grant the user actually went through: the Page sentence
    // tells them to "link a Page", which is not what they just tried to do.
    () => (igDraft ? instagramGrantReturnNotice(grantReturn) : grantReturnNotice(grantReturn)),
  );
  // A failed page lookup is NOT "this account has no pages". Both used to
  // render as "No pages found", so a Graph error (expired grant, missing
  // permission, rate limit) read as a fact about the user's Facebook account
  // — and they had no way to tell, or to retry.
  const [pagesError, setPagesError] = useState<string | null>(null);
  const [reloadPages, setReloadPages] = useState(0);

  // How the Pages step was left. `null` until it is, and reset when the user
  // goes Back to it so the decision is always the one they last made. Restored
  // from the Instagram draft, whose save must send the decision made before the
  // grant — the user is resumed PAST the Pages step and will not make it again.
  const [pagesOutcome, setPagesOutcome] = useState<PagesOutcome | null>(
    igDraft?.pagesOutcome ?? null,
  );
  // The same decision, waiting to be shown on the Pages step once `/pages` has
  // answered (so Back shows the user's answer, not a fresh seed). One-shot.
  const pendingPagesAnswerRef = useRef<PagesOutcome | null>(igDraft?.pagesOutcome ?? null);

  // --- Instagram step (VIBE-1990) ---
  const [igStep, setIgStep] = useState<InstagramStepState>(EMPTY_INSTAGRAM_STEP);
  const [igStatus, setIgStatus] = useState<InstagramListStatus>(igDraft ? 'loading' : 'idle');
  // A failed listing is NOT "no Instagram accounts" — the `pagesError` lesson.
  // Non-fatal: the step still offers Skip and "Try again".
  const [igError, setIgError] = useState<string | null>(null);
  // How the Instagram step was left — `pagesOutcome`'s twin, with the same reset
  // on Back.
  const [instagramOutcome, setInstagramOutcome] = useState<InstagramOutcome | null>(null);
  const [grantingInstagram, setGrantingInstagram] = useState(false);
  const [igGrantError, setIgGrantError] = useState<string | null>(null);
  // The automatic listing runs at most once per mount: a failure is retried by
  // the user ("Try again"), never by walking back onto the step.
  const igRequestedRef = useRef(false);
  // Which listing is the current one, so a superseded answer cannot land.
  const igGenerationRef = useRef(0);
  // The ticks carried across the Instagram grant, for the FIRST listing that
  // succeeds after it (decision D7). One-shot.
  const igResumedRef = useRef<InstagramResumedSelection | null>(
    igDraft
      ? { selected: igDraft.instagramSelected, visibleIds: igDraft.instagramVisibleIds }
      : null,
  );

  // The drafts have done their job the moment they seeded the state above;
  // leaving either behind would force a resumed step again after an unrelated
  // refresh. Both go, whichever one won.
  useEffect(() => {
    if (igDraft || pageDraft) {
      clearPageGrantDraft();
      clearInstagramGrantDraft();
    }
  }, [igDraft, pageDraft]);

  /** This session is finished — not for loading, not for saving.
   *
   *  🔴 The ONE place that raises the fatal flag. A session that cannot be loaded
   *  from and a session that cannot be saved to are the same condition, and the
   *  arms reach it by different routes: the load arm when `/ad-accounts` or
   *  `/pages` fails, the Instagram listing on a terminal status (it is otherwise
   *  non-fatal), the save arm on a terminal status from `/save`. "Try again"
   *  re-runs the load, so a session that dies while a non-fatal banner is up
   *  moves the user from one state to the other through this same function —
   *  which is why it must be one function and not two lookalikes.
   *
   *  It also clears the step notices: they describe things to try, and there is
   *  nothing left to try. */
  const failSession = (message: string) => {
    setSaveError(null);
    setGrantNotice(null);
    setError(message);
  };

  /** The two banners share one slot, so they share one lifetime: whatever is on
   *  screen is about the step you are on. Cleared on every navigation. */
  const clearStepNotices = () => {
    setSaveError(null);
    setGrantNotice(null);
  };

  useEffect(() => {
    let alive = true;
    Promise.all([
      fetchMetaAdAccounts(sessionId),
      fetchMetaPages(sessionId).catch((err: unknown) => {
        if (alive) {
          setPagesError(
            err instanceof Error ? err.message : 'Could not load your Facebook Pages.',
          );
        }
        // Swallowed on purpose: ad accounts still load, so the user can finish
        // connecting. The error above is what keeps it visible. The fallback
        // keeps `linked_page_ids: null` — we know nothing about the baseline, so
        // skipping the step cannot be read as clearing it.
        return null;
      }),
    ])
      .then(([accounts, pagesResp]) => {
        if (!alive) return;
        setAdAccounts(accounts.ad_accounts || []);
        // 🔴 One assignment, and `pagesStepFromResponse` establishes the baseline
        // together with the seed. Every re-seed — first load, the return from the
        // Page re-grant, Try again — goes through here, so none of them can leave
        // a stale baseline behind. After the Instagram grant, `restorePagesAnswer`
        // puts the user's earlier Pages answer back on the ticks (never on the
        // baseline), once.
        if (pagesResp) {
          const restoredAnswer = pendingPagesAnswerRef.current;
          pendingPagesAnswerRef.current = null;
          setPagesStep(restorePagesAnswer(pagesStepFromResponse(pagesResp), restoredAnswer));
          // Unconditionally: a successful response carrying zero Pages is still
          // an answer, and leaving the previous error standing would keep
          // telling the user the lookup failed when it has just succeeded.
          setPagesError(null);
        } else {
          setPagesStep(EMPTY_PAGES_STEP);
        }
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        failSession(err instanceof Error ? err.message : 'Failed to load Meta entities');
        setLoading(false);
      });
    return () => { alive = false; };
  }, [sessionId, reloadPages]);

  // Accounts matching the current search (by name, case-insensitive).
  const normalizedAccountSearch = accountSearch.trim().toLowerCase();
  const filteredAccounts = normalizedAccountSearch
    ? adAccounts.filter((a) => a.name.toLowerCase().includes(normalizedAccountSearch))
    : adAccounts;
  const allFilteredSelected =
    filteredAccounts.length > 0 &&
    filteredAccounts.every((a) => selectedAccounts.includes(a.id));

  // Pages matching the current search.
  const normalizedPageSearch = pageSearch.trim().toLowerCase();
  const filteredPages = normalizedPageSearch
    ? pagesStep.pages.filter((p) => pageLabel(p).toLowerCase().includes(normalizedPageSearch))
    : pagesStep.pages;
  const selectablePageIds = filteredPages.map((p) => p.id);
  const allFilteredPagesSelected =
    selectablePageIds.length > 0 &&
    selectablePageIds.every((id) => pagesStep.selected.includes(id));

  // Every listed Instagram account — the step has no search, so Select All
  // covers the whole list.
  const listedIgIds = igStep.accounts.map((a) => a.id);
  const allListedIgSelected =
    listedIgIds.length > 0 && listedIgIds.every((id) => igStep.selected.includes(id));

  // Pixels matching the current search.
  const normalizedPixelSearch = pixelSearch.trim().toLowerCase();
  const filteredPixels = normalizedPixelSearch
    ? availablePixels.filter((p) => p.name.toLowerCase().includes(normalizedPixelSearch))
    : availablePixels;
  const allFilteredPixelsSelected =
    filteredPixels.length > 0 &&
    filteredPixels.every((p) => selectedPixels.some((s) => pixelKey(s) === pixelKey(p)));

  // --- Step 1: Ad Accounts ---

  const handleAccountToggle = (accountId: string) => {
    setSelectedAccounts((prev) =>
      prev.includes(accountId) ? prev.filter((id) => id !== accountId) : [...prev, accountId],
    );
  };

  const handleSelectAllAccounts = () => {
    const filteredIds = filteredAccounts.map((a) => a.id);
    if (allFilteredSelected) {
      setSelectedAccounts((prev) => prev.filter((id) => !filteredIds.includes(id)));
    } else {
      setSelectedAccounts((prev) => Array.from(new Set([...prev, ...filteredIds])));
    }
  };

  // --- Step 2: Pages ---

  const handlePageToggle = (pageId: string) => {
    setPagesStep((prev) => togglePage(prev, pageId));
  };

  const handleSelectAllPages = () => {
    setPagesStep((prev) => toggleAllPages(prev, selectablePageIds));
  };

  /** Re-enter Meta's consent dialog for the Page permissions (VIBE-1916).
   *
   *  A full server-side redirect, so the wizard unmounts: park the ad-account
   *  selection first or the user comes back to an empty step 1. The session is
   *  validated server-side before the URL is issued, so a dead one fails here
   *  rather than after a pointless trip through Meta. */
  const handleGrantPages = async () => {
    setGranting(true);
    setGrantError(null);
    setGrantNotice(null);
    writePageGrantDraft({ sessionId, adAccountIds: selectedAccounts });
    try {
      const { auth_url } = await grantMetaPages(sessionId);
      window.location.href = auth_url;
    } catch (err: unknown) {
      clearPageGrantDraft();
      setGranting(false);
      // A dead session (404 / 410 / 403 / 401 from `require_oauth_session`) is
      // not a step error: the save would fail identically. One place raises it.
      if (isSessionTerminalStatus(err instanceof ApiError ? err.status : null)) {
        failSession(err instanceof Error ? err.message : 'This connection attempt has ended.');
        return;
      }
      setGrantError(
        err instanceof ApiError && err.detail
          ? err.detail
          : 'Could not start the Facebook Page permission request. You can skip this step.',
      );
    }
  };

  // --- Step 3: Instagram accounts (VIBE-1990) ---

  /** Apply a listing, if it is still the current one.
   *
   *  The ticks carried across the Instagram grant are applied to the first
   *  listing that SUCCEEDS — a failed one does not consume them, so "Try again"
   *  still restores what the user had. */
  const onInstagramListed = (generation: number, resp: MetaInstagramAccountsResponse) => {
    if (generation !== igGenerationRef.current) return;
    const next = instagramStepFromResponse(resp, igResumedRef.current);
    igResumedRef.current = null;
    setIgStep(next);
    setIgError(null);
    setIgStatus('loaded');
  };

  /** A failed listing. Non-fatal — `EMPTY_INSTAGRAM_STEP` knows nothing, so
   *  nothing on the step can clear — unless the SESSION is what failed, which
   *  no retry can fix and which the save would then hit identically.
   *
   *  `endSession` is always `failSession`, passed in rather than captured: a
   *  helper that captures only setters and refs is stable for the hooks linter,
   *  one that captures another component function is not, and the resume effect
   *  below calls this one. */
  const onInstagramListFailed = (
    generation: number,
    err: unknown,
    endSession: (message: string) => void,
  ) => {
    if (generation !== igGenerationRef.current) return;
    const message = err instanceof Error ? err.message : 'Could not load your Instagram accounts.';
    setIgStep(EMPTY_INSTAGRAM_STEP);
    setIgStatus('failed');
    if (isSessionTerminalStatus(err instanceof ApiError ? err.status : null)) {
      endSession(message);
      return;
    }
    setIgError(message);
  };

  /** Show the spinner and list. Used on first entry and by "Try again". */
  const loadInstagramAccounts = () => {
    setIgStatus('loading');
    setIgError(null);
    const generation = ++igGenerationRef.current;
    fetchMetaInstagramAccounts(sessionId)
      .then((resp) => onInstagramListed(generation, resp))
      .catch((err: unknown) => onInstagramListFailed(generation, err, failSession));
  };

  /** The automatic listing: lazily, on the first entry to the step, and at most
   *  once per mount. Nobody who cancels earlier pays for Meta's per-business
   *  fan-out, and a failure is not re-requested on every visit. */
  const ensureInstagramListing = () => {
    if (igRequestedRef.current) return;
    igRequestedRef.current = true;
    loadInstagramAccounts();
  };

  const handleInstagramToggle = (accountId: string) => {
    setIgStep((prev) => toggleInstagramAccount(prev, accountId));
  };

  const handleSelectAllInstagram = () => {
    setIgStep((prev) => toggleAllInstagramAccounts(prev, listedIgIds));
  };

  /** Re-enter Meta's consent dialog for the Instagram permission — the step's
   *  OWN grant (base + `instagram_basic`), not the Page one.
   *
   *  A full server-side redirect, so the wizard unmounts: park everything decided
   *  so far — the ad accounts, how the Pages step was left, and this step's ticks
   *  together with the rows they were made against (D7 needs both). Every row of
   *  the list counts as visible: the step has no search, so every row is on
   *  screen. */
  const handleGrantInstagram = async () => {
    setGrantingInstagram(true);
    setIgGrantError(null);
    setGrantNotice(null);
    writeInstagramGrantDraft({
      sessionId,
      adAccountIds: selectedAccounts,
      pagesOutcome,
      instagramSelected: igStep.selected,
      instagramVisibleIds: igStep.accounts.map((a) => a.id),
    });
    try {
      const { auth_url } = await grantMetaInstagram(sessionId);
      window.location.href = auth_url;
    } catch (err: unknown) {
      clearInstagramGrantDraft();
      setGrantingInstagram(false);
      // The `handleGrantPages` rule: a dead session goes through `failSession`.
      if (isSessionTerminalStatus(err instanceof ApiError ? err.status : null)) {
        failSession(err instanceof Error ? err.message : 'This connection attempt has ended.');
        return;
      }
      setIgGrantError(
        err instanceof ApiError && err.detail
          ? err.detail
          : 'Could not start the Instagram permission request. You can skip this step.',
      );
    }
  };

  // --- Step 4: Pixels ---

  const isPixelSelected = (pixel: FlatPixel) =>
    selectedPixels.some((p) => pixelKey(p) === pixelKey(pixel));

  const handlePixelToggle = (pixel: FlatPixel) => {
    setSelectedPixels((prev) =>
      prev.some((p) => pixelKey(p) === pixelKey(pixel))
        ? prev.filter((p) => pixelKey(p) !== pixelKey(pixel))
        : [...prev, pixel],
    );
  };

  const handleSelectAllPixels = () => {
    const isFiltered = (p: FlatPixel) => filteredPixels.some((fp) => pixelKey(fp) === pixelKey(p));
    if (allFilteredPixelsSelected) {
      setSelectedPixels((prev) => prev.filter((p) => !isFiltered(p)));
    } else {
      setSelectedPixels((prev) => {
        const merged = [...prev];
        for (const pixel of filteredPixels) {
          if (!merged.some((p) => pixelKey(p) === pixelKey(pixel))) merged.push(pixel);
        }
        return merged;
      });
    }
  };

  // --- Pixel fetching (backend proxies the Graph API per account) ---

  /** The network half of the prefetch. It writes state only when the requests
   *  answer, which is what lets the Instagram resume start it from an effect;
   *  the synchronous half below is what that resume initialises instead. */
  const requestPixels = (sid: string, accountIds: readonly string[], generation: number) => {
    Promise.all(
      accountIds.map((accountId) =>
        fetchMetaPixels(sid, accountId)
          .then((res) =>
            (res.pixels || []).map((p): FlatPixel => ({
              id: p.id,
              name: p.name || `Pixel ${p.id}`,
              adAccountId: accountId,
            })),
          )
          // A single account failing shouldn't drop the others' pixels.
          .catch(() => null),
      ),
    ).then((results) => {
      if (generation !== pixelGenerationRef.current) return; // superseded
      const succeeded = results.filter((r): r is FlatPixel[] => r !== null);
      if (succeeded.length === 0 && results.length > 0) {
        fetchedForAccountsRef.current = ''; // let re-entering the step retry
        setPixelFetchError('Failed to load pixels. You can skip this step.');
      } else {
        setAvailablePixels(succeeded.flat());
      }
      setLoadingPixels(false);
    });
  };

  const fetchPixelsForSelectedAccounts = () => {
    const accountsKey = pixelAccountsKey(selectedAccounts);
    if (accountsKey === fetchedForAccountsRef.current) return; // Already fetched for these accounts
    // Claimed before the request, not after it: resuming after a grant means
    // this is also reached from the Pages and Instagram steps, and a second
    // call while the first is in flight would drop the results of both.
    fetchedForAccountsRef.current = accountsKey;
    const generation = ++pixelGenerationRef.current;

    setLoadingPixels(true);
    setPixelFetchError(null);
    setAvailablePixels([]);
    setSelectedPixels([]);

    requestPixels(sessionId, selectedAccounts, generation);
  };

  // Resuming on the Instagram step after its grant (decision D12). The steps
  // that normally start the listing and the pixel prefetch were not walked in
  // this mount, so both start here — the listing as this mount's one automatic
  // listing, carrying the parked ticks.
  //
  // 🔴 Requests only, no synchronous state write: `igStatus` and
  // `loadingPixels` were initialised to "loading" from the draft, and every
  // other write happens when a request answers. The pixel claim mirrors the one
  // in `fetchPixelsForSelectedAccounts`, so a later call for the same accounts
  // is a no-op rather than a second, result-dropping request.
  useEffect(() => {
    if (!igDraft || igRequestedRef.current) return;
    igRequestedRef.current = true;
    const igGeneration = ++igGenerationRef.current;
    fetchMetaInstagramAccounts(sessionId)
      .then((resp) => onInstagramListed(igGeneration, resp))
      .catch((err: unknown) => onInstagramListFailed(igGeneration, err, failSession));
    fetchedForAccountsRef.current = pixelAccountsKey(igDraft.adAccountIds);
    requestPixels(sessionId, igDraft.adAccountIds, ++pixelGenerationRef.current);
  }, [sessionId, igDraft]);

  // --- Navigation ---

  const leavePagesStep = (outcome: PagesOutcome) => {
    clearStepNotices();
    setPagesOutcome(outcome);
    // Idempotent; needed here because resuming after the Page re-grant skips step 1.
    fetchPixelsForSelectedAccounts();
    ensureInstagramListing();
    setCurrentStep(STEP_INSTAGRAM);
  };

  const leaveInstagramStep = (outcome: InstagramOutcome) => {
    clearStepNotices();
    setInstagramOutcome(outcome);
    // Idempotent, like the call in `leavePagesStep`: Pixels is never entered
    // without a prefetch for the accounts currently selected.
    fetchPixelsForSelectedAccounts();
    setCurrentStep(STEP_PIXELS);
  };

  const handleNext = () => {
    if (currentStep === STEP_ACCOUNTS) {
      clearStepNotices();
      // Start fetching pixels in background while user selects pages
      fetchPixelsForSelectedAccounts();
      setCurrentStep(STEP_PAGES);
    } else if (currentStep === STEP_PAGES) {
      leavePagesStep({ skipped: false, pageIds: pagesStep.selected });
    } else if (currentStep === STEP_INSTAGRAM) {
      leaveInstagramStep({ skipped: false, ids: igStep.selected });
    }
  };

  /** Skip leaves the step WITHOUT touching the selection.
   *
   *  🔴 This is where we differ from mvp, and the difference is load-bearing for
   *  `handleBack` below: mvp's Skip CLEARS the picked Pages, which is why mvp has
   *  to re-seed on Back. Ours clears nothing, so there is nothing to repair and
   *  Back deliberately does not re-seed. Anyone "restoring symmetry" by making
   *  this clear the selection MUST add the re-seed to `handleBack` in the same
   *  edit — otherwise Back returns the user to a silently emptied step whose
   *  baseline still claims their Pages are linked, and Next then offers a clear
   *  they never asked for. */
  const handleSkipPages = () => leavePagesStep({ skipped: true });

  /** Skip leaves the Instagram step WITHOUT touching the selection — the AUDIT's
   *  Pages model (`handleSkipPages`), not mvp's. The same pairing holds: because
   *  Skip clears nothing, Back into this step does not re-seed, and making either
   *  one mvp-like without the other reintroduces mvp's bug. */
  const handleSkipInstagram = () => leaveInstagramStep({ skipped: true });

  const handleBack = () => {
    // Returning to a step retracts the decision made when leaving it, so
    // Skip/Next is always the one the user last chose — not the first one:
    // Instagram → Pages retracts the Pages decision, Pixels → Instagram the
    // Instagram one.
    //
    // 🔴 It deliberately does NOT re-seed either selection from `linked`. mvp
    // does, because mvp's Skip empties the picker and the re-seed puts it back;
    // ours touches nothing, so re-seeding here would instead DISCARD the ticks
    // the user made before stepping forward. See `handleSkipPages` and
    // `handleSkipInstagram` — each pair is one decision, and changing either half
    // alone reintroduces mvp's bug.
    if (currentStep === STEP_INSTAGRAM) setPagesOutcome(null);
    if (currentStep === STEP_PIXELS) setInstagramOutcome(null);
    clearStepNotices();
    setCurrentStep((s) => Math.max(STEP_ACCOUNTS, s - 1));
  };

  const save = async (pixels: FlatPixel[]) => {
    setSaving(true);
    setError('');
    setSaveError(null);
    try {
      // The page key's and the Instagram key's PRESENCE is the third value:
      // absent = skipped. Built in `buildMetaSavePayloadWithInstagram` (which
      // wraps `buildMetaSavePayload`) rather than here, so neither rule that can
      // erase a user's links is a spread buried in an argument list.
      const result = await saveMetaSelections(buildMetaSavePayloadWithInstagram({
        sessionId,
        adAccountIds: selectedAccounts,
        pagesOutcome,
        instagramOutcome,
        pixels: pixels.map((p) => ({
          pixel_id: p.id,
          pixel_name: p.name,
          ad_account_id: p.adAccountId,
        })),
      }));
      clearPageGrantDraft();
      clearInstagramGrantDraft();
      onSaved(result.count);
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save Meta selection';
      // A dead session is not something the user can retry past: `/save`,
      // `/pages` and `/pixels` all go through the same guard, so every button
      // left on screen would lead back to this exact message. Say it once, in
      // the one state that means it, and offer the only move that helps.
      if (isSessionTerminalStatus(err instanceof ApiError ? err.status : null)) {
        failSession(message);
        setSaving(false);
        return;
      }
      // Everything else is `saveError`, never `error`: the save failed but every
      // control still works, and gating the layout on it produces a dialog with
      // nothing in it but a close button. See the state declaration.
      setSaveError(message);
      // Each pair of structured refusals is about ONE selection — the Pages or
      // the Instagram one — and the server refuses rather than writing an empty
      // list, which would read as a deliberate clear. Send the user back to the
      // step they can actually fix, instead of leaving them on Pixels with a
      // message about another step. The message travels with them, rendered
      // inside the step body.
      if (
        err instanceof ApiError &&
        (err.code === META_SAVE_ERRORS.pagesUnlinkable ||
          err.code === META_SAVE_ERRORS.pagesNotGranted)
      ) {
        setPagesOutcome(null);
        // The Instagram step lies AHEAD of the Pages step again, so its decision
        // is retracted too: an outcome exists only for the steps behind the
        // current one. (Step 3 must be left again before any save anyway.)
        setInstagramOutcome(null);
        setCurrentStep(STEP_PAGES);
      } else if (
        // The Instagram twins of the two refusals above, for the same reason and
        // with the same move: back to the step the user can fix.
        err instanceof ApiError &&
        (err.code === META_SAVE_ERRORS.instagramUnlinkable ||
          err.code === META_SAVE_ERRORS.instagramNotGranted)
      ) {
        setInstagramOutcome(null);
        setCurrentStep(STEP_INSTAGRAM);
      }
      setSaving(false);
    }
  };

  const handleConfirm = () => void save(selectedPixels);
  const handleSkipPixels = () => void save([]);

  // --- Validation ---

  const nextEnabled = pagesNextEnabled(pagesStep);
  // Only over a listing that answered. Every other state holds
  // `EMPTY_INSTAGRAM_STEP`, which the rule refuses anyway; the status makes it
  // explicit rather than a property of the placeholder.
  const igNextEnabled = igStatus === 'loaded' && instagramNextEnabled(igStep);

  const canNext = () => {
    if (currentStep === STEP_ACCOUNTS) return selectedAccounts.length > 0;
    // 🔴 The VIBE-1897 rule: a real CHANGE, not a non-empty selection. Skip is
    // what leaves the step without touching anything, and it is always available
    // — including when the lookup failed, when the permission is absent, and when
    // the account simply has no Pages.
    if (currentStep === STEP_PAGES) return nextEnabled;
    // The same rule, from the twin module.
    if (currentStep === STEP_INSTAGRAM) return igNextEnabled;
    return true;
  };

  // --- Group pixels by ad account for display ---

  const pixelsByAccount = filteredPixels.reduce<
    Record<string, { accountName: string; pixels: FlatPixel[] }>
  >((acc, pixel) => {
    if (!acc[pixel.adAccountId]) {
      const account = adAccounts.find((a) => a.id === pixel.adAccountId);
      acc[pixel.adAccountId] = {
        accountName: account?.name || pixel.adAccountId,
        pixels: [],
      };
    }
    acc[pixel.adAccountId].pixels.push(pixel);
    return acc;
  }, {});

  // --- Footer info ---

  const getSelectionCount = () => {
    if (currentStep === STEP_ACCOUNTS)
      return `${selectedAccounts.length} account${selectedAccounts.length !== 1 ? 's' : ''} selected`;
    if (currentStep === STEP_INSTAGRAM) return instagramSelectionCount(igStep.selected.length);
    if (currentStep === STEP_PAGES) {
      const n = pagesStep.selected.length;
      const suffix = nextEnabled
        ? ''
        : ` · skipping leaves ${pagesStep.linkedPageIds === null ? 'existing links' : 'your links'} unchanged`;
      return `${n} page${n !== 1 ? 's' : ''} selected${suffix}`;
    }
    return `${selectedPixels.length} pixel${selectedPixels.length !== 1 ? 's' : ''} selected`;
  };

  // Shared search box used on every step except Instagram.
  const renderSearch = ({
    value,
    onChange,
    placeholder,
    ariaLabel,
  }: {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    ariaLabel: string;
  }) => (
    <div className="relative mb-3">
      <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className="pl-9 pr-9"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear search"
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      )}
    </div>
  );

  const renderCheckboxRow = ({
    key,
    checked,
    onToggle,
    title,
    subtitle,
    disabled = false,
    disabledReason,
  }: {
    key: string;
    checked: boolean;
    onToggle: () => void;
    title: string;
    subtitle?: string | null;
    disabled?: boolean;
    disabledReason?: string;
  }) => (
    <label
      key={key}
      className={cn(
        'flex items-center gap-3 rounded-lg border p-3 transition-colors',
        disabled
          ? 'cursor-not-allowed border-border bg-muted/40 opacity-70'
          : 'cursor-pointer ' +
            (checked ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'),
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        disabled={disabled}
        className="size-4 shrink-0 accent-primary disabled:cursor-not-allowed"
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{title}</p>
        {subtitle && <p className="truncate text-xs text-muted-foreground">{subtitle}</p>}
        {disabled && disabledReason && (
          <p className="mt-0.5 text-xs text-muted-foreground">{disabledReason}</p>
        )}
      </div>
    </label>
  );

  const emptyNote = (text: string) => (
    <p className="rounded-lg bg-muted/50 py-4 text-center text-sm text-muted-foreground">{text}</p>
  );

  // The on-demand Page grant: offered when the connection carries no Page
  // permission. `pages` is then always empty and no Graph call was made — this
  // is a missing permission, never an error and never "you have no Pages".
  const renderGrantPages = () => (
    <div className="rounded-lg border border-border bg-muted/40 px-4 py-3">
      <p className="text-sm text-foreground">Connect a Facebook Page (optional)</p>
      <p className="mt-1 text-xs text-muted-foreground">{grantPromptText(pagesGrantReturn)}</p>
      {pagesStep.linkedPageIds !== null && pagesStep.linkedPageIds.length > 0 && (
        <p className="mt-2 text-xs text-muted-foreground">
          {pagesStep.linkedPageIds.length} Page
          {pagesStep.linkedPageIds.length !== 1 ? 's are' : ' is'} already linked and will be left
          exactly as {pagesStep.linkedPageIds.length !== 1 ? 'they are' : 'it is'}.
        </p>
      )}
      {grantError && <p className="mt-2 text-xs text-destructive">{grantError}</p>}
      <Button
        variant="outline"
        size="sm"
        className="mt-3"
        onClick={() => void handleGrantPages()}
        disabled={granting || saving}
      >
        {granting ? (
          <>
            <Loader2 className="size-4 animate-spin" />
            Opening Facebook...
          </>
        ) : (
          'Connect a Facebook Page'
        )}
      </Button>
    </div>
  );

  // Linked Pages that pressing Next right now would unlink. Empty whenever Next
  // is refused, so the panel below never warns about a save that cannot happen.
  const droppedByReplace = pagesDroppedByReplace(pagesStep);

  // A linked Page we cannot show — vanished from the list. The baseline is then
  // unreproducible, so an empty selection is not a clear; naming the ids is the
  // only way the user can tell which Page it is.
  //
  // 🔴 Two states, and the second one is the point. Reassurance alone ("unticking
  // won't unlink", "skipping leaves them") is true but is the ONLY mention of
  // unlinking anywhere in this wizard — and it says it will not happen. It is
  // read in the one state where continuing destroys a link for good, because a
  // replace cannot carry a Page the picker cannot show.
  const renderUnavailableLinks = () => {
    const ids = pagesStep.unavailableLinkedPageIds;
    const many = ids.length !== 1;
    const replacing = droppedByReplace.length > 0;
    return (
      <div
        className={cn(
          'mb-3 rounded-lg border px-4 py-3',
          replacing ? 'border-destructive/40 bg-destructive/5' : 'border-border bg-muted/40',
        )}
      >
        <p className="text-sm text-foreground">
          {replacing
            ? `Continuing will unlink ${droppedByReplace.length} Page${droppedByReplace.length !== 1 ? 's' : ''}`
            : `${ids.length} linked Page${many ? 's are' : ' is'} not reachable right now`}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {(replacing ? droppedByReplace : ids).join(', ')}
        </p>
        <p className="mt-2 text-xs text-muted-foreground">
          {replacing ? (
            <>
              Next replaces your linked Pages with the ones ticked here, and we can&apos;t show
              {droppedByReplace.length !== 1 ? ' these' : ' this one'}, so
              {droppedByReplace.length !== 1 ? ' they' : ' it'} can&apos;t be carried over
              &mdash; {droppedByReplace.length !== 1 ? 'they' : 'it'} will be unlinked. Skip instead
              to leave every link exactly as it is.
            </>
          ) : (
            <>
              Because we can&apos;t show {many ? 'them' : 'it'} here, unticking everything
              won&apos;t unlink your Pages &mdash; skipping leaves them as they are.
            </>
          )}
        </p>
      </div>
    );
  };

  // Linked Instagram accounts that pressing Next right now would unlink — the
  // `droppedByReplace` twin, empty whenever Next is refused.
  const igDroppedByReplace = instagramDroppedByReplace(igStep);

  // `renderUnavailableLinks` with the noun swapped, and for the same two states.
  //
  // 🔴 The only Instagram state allowed out of the neutral styling: once
  // `igDroppedByReplace` is non-empty, Next REPLACES the linked set with the ticks
  // and the server carries nothing it cannot resolve, so the ids named here are
  // unlinked for good. Everything else on this step is a missing permission, a
  // partial answer or an empty business — none of them an error.
  const renderUnavailableInstagramLinks = () => {
    const ids = igStep.unavailableLinkedIds;
    const many = ids.length !== 1;
    const replacing = igDroppedByReplace.length > 0;
    const manyDropped = igDroppedByReplace.length !== 1;
    return (
      <div
        className={cn(
          'mb-3 rounded-lg border px-4 py-3',
          replacing ? 'border-destructive/40 bg-destructive/5' : 'border-border bg-muted/40',
        )}
      >
        <p className="text-sm text-foreground">
          {replacing
            ? `Continuing will unlink ${igDroppedByReplace.length} Instagram account${manyDropped ? 's' : ''}`
            : `${ids.length} linked Instagram account${many ? 's are' : ' is'} not reachable right now`}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {(replacing ? igDroppedByReplace : ids).join(', ')}
        </p>
        <p className="mt-2 text-xs text-muted-foreground">
          {replacing ? (
            <>
              Next replaces your linked Instagram accounts with the ones ticked here, and we
              can&apos;t show{manyDropped ? ' these' : ' this one'}, so
              {manyDropped ? ' they' : ' it'} can&apos;t be carried over
              &mdash; {manyDropped ? 'they' : 'it'} will be unlinked. Skip instead
              to leave every link exactly as it is.
            </>
          ) : (
            <>
              Because we can&apos;t show {many ? 'them' : 'it'} here, unticking everything
              won&apos;t unlink your Instagram accounts &mdash; skipping leaves them as they are.
            </>
          )}
        </p>
      </div>
    );
  };

  /** The grey notice style shared by every non-destructive Instagram message. */
  const neutralNotice = (text: string) => (
    <div className="mb-3 rounded-lg border border-border bg-muted/40 px-4 py-3">
      <p className="text-xs text-muted-foreground">{text}</p>
    </div>
  );

  // The Instagram step's own grant. Its failure message stays neutral like the
  // rest of the step: the connection is intact and Skip still works.
  const renderGrantInstagramButton = () => (
    <>
      {igGrantError && <p className="mt-2 text-xs text-foreground">{igGrantError}</p>}
      <Button
        variant="outline"
        size="sm"
        className="mt-3"
        onClick={() => void handleGrantInstagram()}
        disabled={grantingInstagram || saving}
      >
        {grantingInstagram ? (
          <>
            <Loader2 className="size-4 animate-spin" />
            Opening Facebook...
          </>
        ) : (
          INSTAGRAM_GRANT_BUTTON_LABEL
        )}
      </Button>
    </>
  );

  const renderInstagramStep = () => {
    if (igStatus === 'idle' || igStatus === 'loading') {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="mb-3 size-8 animate-spin text-foreground" />
          <p className="text-sm">{INSTAGRAM_LOADING_TEXT}</p>
        </div>
      );
    }

    // The request itself failed — neither "no permission" nor "no accounts", and
    // said so, because both of those have their own, different, state below.
    if (igStatus === 'failed') {
      return (
        <div className="rounded-lg border border-border bg-muted/40 px-4 py-3">
          <p className="text-sm text-foreground">We couldn&apos;t load your Instagram accounts.</p>
          {igError && <p className="mt-1 text-xs text-muted-foreground">{igError}</p>}
          <p className="mt-2 text-xs text-muted-foreground">
            This is not the same as having no Instagram accounts. You can skip this step
            &mdash; nothing that&apos;s connected will change.
          </p>
          <button
            type="button"
            onClick={loadInstagramAccounts}
            className="mt-2 cursor-pointer text-xs font-medium text-foreground underline underline-offset-2"
          >
            Try again
          </button>
        </div>
      );
    }

    const empty = instagramEmptyState(igStep);

    // No permission: nothing was listed and nothing can be selected, so every
    // existing link is left exactly as it is — the `renderGrantPages` card.
    if (!igStep.scopeGranted && empty) {
      const leftAsIs = instagramLinkedLeftAsIsText(igStep.linkedIds);
      return (
        <div className="rounded-lg border border-border bg-muted/40 px-4 py-3">
          <p className="text-sm text-foreground">{empty.title}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {instagramGrantPromptText({
              resumedFromGrant: resumedFromInstagramGrant,
              grantReturn,
            }) ?? empty.body}
          </p>
          {leftAsIs && <p className="mt-2 text-xs text-muted-foreground">{leftAsIs}</p>}
          {renderGrantInstagramButton()}
        </div>
      );
    }

    const unconfirmed = instagramUnconfirmedNotice(igStep);
    const incomplete = instagramIncompleteNotice(igStep);
    return (
      <div>
        {igStep.accounts.length > 0 && (
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold">Instagram Accounts ({igStep.accounts.length})</h3>
            <button
              type="button"
              onClick={handleSelectAllInstagram}
              className="text-xs font-medium text-foreground hover:text-muted-foreground"
            >
              {allListedIgSelected ? 'Deselect All' : 'Select All'}
            </button>
          </div>
        )}

        {igStep.unavailableLinkedIds.length > 0
          ? renderUnavailableInstagramLinks()
          : unconfirmed && neutralNotice(unconfirmed)}
        {incomplete && neutralNotice(incomplete)}

        {empty ? (
          <div className="rounded-lg border border-border bg-muted/40 px-4 py-3">
            <p className="text-sm text-foreground">{empty.title}</p>
            <p className="mt-1 text-xs text-muted-foreground">{empty.body}</p>
            {renderGrantInstagramButton()}
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {igStep.accounts.map((account) =>
                renderCheckboxRow({
                  key: account.id,
                  checked: igStep.selected.includes(account.id),
                  onToggle: () => handleInstagramToggle(account.id),
                  title: instagramAccountTitle(account),
                  subtitle: instagramAccountSubtitle(account),
                }),
              )}
            </div>

            <div className="mt-3 rounded-lg border border-border bg-muted/40 px-4 py-3">
              {INSTAGRAM_LIST_FOOTNOTES.map((line, i) => (
                <p key={line} className={cn('text-xs text-muted-foreground', i > 0 && 'mt-1')}>
                  {line}
                </p>
              ))}
              {renderGrantInstagramButton()}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !saving) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <img src="/logos/meta_ads_logo.svg" alt="Meta" className="size-8 shrink-0 object-contain" />
            <div>
              <DialogTitle>{STEP_TITLES[currentStep - 1]}</DialogTitle>
              <DialogDescription>
                Step {currentStep} of {STEPS.length}{currentStep !== STEP_ACCOUNTS ? ' · Optional' : ''}
              </DialogDescription>
            </div>
          </div>
          {/* Progress Bar */}
          <div className="mt-3 flex gap-2">
            {STEPS.map((step) => (
              <div
                key={step}
                className={cn(
                  'h-1 flex-1 rounded-full transition-colors',
                  step <= currentStep ? 'bg-primary' : 'bg-muted',
                )}
              />
            ))}
          </div>
        </DialogHeader>

        {loading && (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5">
                <Skeleton className="h-4 w-4 shrink-0" />
                <Skeleton className="h-4 max-w-[60%] flex-1" />
                <Skeleton className="h-3 w-16" />
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="py-2">
            <p className="text-sm text-destructive">{error}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              This connection attempt can&apos;t be continued. Close this and connect Meta again
              &mdash; nothing you had linked before has changed.
            </p>
          </div>
        )}

        {!loading && !error && (
          <div className="max-h-[55vh] overflow-y-auto pr-1">
            {/* A failed SAVE, rendered inside the body so the step, Skip, Back
                and Next all stay on screen. It travels with the user to the
                Pages or Instagram step when the server refuses that selection. */}
            {saveError && (
              <div className="mb-3 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3">
                <p className="text-sm text-foreground">We couldn&apos;t finish connecting.</p>
                <p className="mt-1 text-xs text-muted-foreground">{saveError}</p>
              </div>
            )}
            {/* Deliberately NOT destructive styling and deliberately not `error`:
                nothing failed, the previous token is still in place, and every
                control on this step still works. It reports a change that was
                declined, which is a different fact from a broken connection. */}
            {grantNotice && (
              <div className="mb-3 rounded-lg border border-border bg-muted/40 px-4 py-3">
                <p className="text-sm text-foreground">Your ad permission was left unchanged.</p>
                <p className="mt-1 text-xs text-muted-foreground">{grantNotice}</p>
              </div>
            )}
            {/* Step 1: Ad Accounts */}
            {currentStep === STEP_ACCOUNTS && (
              <div>
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-sm font-semibold">Ad Accounts ({adAccounts.length})</h3>
                  {filteredAccounts.length > 0 && (
                    <button
                      type="button"
                      onClick={handleSelectAllAccounts}
                      className="text-xs font-medium text-foreground hover:text-muted-foreground"
                    >
                      {allFilteredSelected ? 'Deselect All' : 'Select All'}
                    </button>
                  )}
                </div>

                {adAccounts.length > 0 &&
                  renderSearch({
                    value: accountSearch,
                    onChange: setAccountSearch,
                    placeholder: 'Search accounts by name',
                    ariaLabel: 'Search ad accounts by name',
                  })}

                {adAccounts.length === 0 ? (
                  emptyNote('No ad accounts found')
                ) : filteredAccounts.length === 0 ? (
                  emptyNote(`No accounts match "${accountSearch.trim()}"`)
                ) : (
                  <div className="space-y-2">
                    {filteredAccounts.map((account) =>
                      renderCheckboxRow({
                        key: account.id,
                        checked: selectedAccounts.includes(account.id),
                        onToggle: () => handleAccountToggle(account.id),
                        title: account.name,
                        subtitle: [account.id, account.currency, account.timezone_name]
                          .filter(Boolean)
                          .join(' · '),
                      }),
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Pages */}
            {currentStep === STEP_PAGES && (
              <div>
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-sm font-semibold">
                    Facebook Pages ({pagesStep.pages.length})
                  </h3>
                  {selectablePageIds.length > 0 && (
                    <button
                      type="button"
                      onClick={handleSelectAllPages}
                      className="text-xs font-medium text-foreground hover:text-muted-foreground"
                    >
                      {allFilteredPagesSelected ? 'Deselect All' : 'Select All'}
                    </button>
                  )}
                </div>

                {pagesStep.unavailableLinkedPageIds.length > 0 &&
                  pagesStep.scopeGranted &&
                  renderUnavailableLinks()}

                {pagesStep.pages.length > 0 &&
                  renderSearch({
                    value: pageSearch,
                    onChange: setPageSearch,
                    placeholder: 'Search pages by name',
                    ariaLabel: 'Search Facebook pages by name',
                  })}

                {!pagesStep.scopeGranted ? (
                  renderGrantPages()
                ) : pagesStep.pages.length === 0 && pagesError ? (
                  <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3">
                    <p className="text-sm text-foreground">
                      We couldn&apos;t load your Facebook Pages.
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">{pagesError}</p>
                    <p className="mt-2 text-xs text-muted-foreground">
                      This is not the same as having no Pages. You can continue
                      with ad accounts only, but without a Page we can&apos;t
                      profile your brand&apos;s public presence.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        // Without `loading` the empty branch renders "No pages
                        // found" for the duration of the retry — precisely the
                        // claim this whole error branch exists to avoid making.
                        setPagesError(null);
                        setLoading(true);
                        setReloadPages((n) => n + 1);
                      }}
                      className="mt-2 cursor-pointer text-xs font-medium text-foreground underline underline-offset-2"
                    >
                      Try again
                    </button>
                  </div>
                ) : pagesStep.pages.length === 0 ? (
                  emptyNote('No pages found')
                ) : filteredPages.length === 0 ? (
                  emptyNote(`No pages match "${pageSearch.trim()}"`)
                ) : (
                  <div className="space-y-2">
                    {filteredPages.map((page) =>
                      renderCheckboxRow({
                        key: page.id,
                        checked: pagesStep.selected.includes(page.id),
                        onToggle: () => handlePageToggle(page.id),
                        title: pageLabel(page),
                        subtitle: page.category,
                      }),
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Instagram accounts */}
            {currentStep === STEP_INSTAGRAM && renderInstagramStep()}

            {/* Step 4: Pixels */}
            {currentStep === STEP_PIXELS && (
              <div>
                {loadingPixels ? (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <Loader2 className="mb-3 size-8 animate-spin text-foreground" />
                    <p className="text-sm">Loading pixels for selected ad accounts...</p>
                  </div>
                ) : pixelFetchError ? (
                  <div className="py-8 text-center">
                    <p className="text-sm text-muted-foreground">{pixelFetchError}</p>
                  </div>
                ) : availablePixels.length === 0 ? (
                  <div className="py-8 text-center">
                    <p className="text-sm text-foreground">
                      No pixels found for the selected ad accounts.
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      You can skip this step and add pixels later.
                    </p>
                  </div>
                ) : (
                  <div>
                    <div className="mb-3 flex items-center justify-between">
                      <h3 className="text-sm font-semibold">
                        Available Pixels ({availablePixels.length})
                      </h3>
                      {filteredPixels.length > 0 && (
                        <button
                          type="button"
                          onClick={handleSelectAllPixels}
                          className="text-xs font-medium text-foreground hover:text-muted-foreground"
                        >
                          {allFilteredPixelsSelected ? 'Deselect All' : 'Select All'}
                        </button>
                      )}
                    </div>

                    {renderSearch({
                      value: pixelSearch,
                      onChange: setPixelSearch,
                      placeholder: 'Search pixels by name',
                      ariaLabel: 'Search pixels by name',
                    })}

                    {filteredPixels.length === 0 ? (
                      emptyNote(`No pixels match "${pixelSearch.trim()}"`)
                    ) : (
                      <div className="space-y-4">
                        {Object.entries(pixelsByAccount).map(([accountId, group]) => (
                          <div key={accountId}>
                            {Object.keys(pixelsByAccount).length > 1 && (
                              <p className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                                {group.accountName}
                              </p>
                            )}
                            <div className="space-y-2">
                              {group.pixels.map((pixel) =>
                                renderCheckboxRow({
                                  key: pixelKey(pixel),
                                  checked: isPixelSelected(pixel),
                                  onToggle: () => handlePixelToggle(pixel),
                                  title: pixel.name,
                                  subtitle: `ID: ${pixel.id}`,
                                }),
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* The fatal state names a next step ("reconnect"), so it has to offer
            one: without this the dialog leaves only the window's close control
            for a session that can never answer again. */}
        {error && (
          <div className="flex items-center justify-end border-t border-border pt-3">
            <Button variant="outline" onClick={onClose}>Close</Button>
          </div>
        )}

        {/* Footer */}
        {!loading && !error && (
          <div className="flex items-center justify-between gap-2 border-t border-border pt-3">
            <div className="min-w-0">
              <p className="text-sm text-muted-foreground">{getSelectionCount()}</p>
              {/* 🔴 The region is MOUNTED for the whole Instagram step and its
                  TEXT is what changes: a live region added to the document
                  already holding its message is widely not announced. An empty
                  `<p>` costs no height. */}
              {currentStep === STEP_INSTAGRAM && (
                <p className="mt-0.5 text-xs text-muted-foreground" aria-live="polite">
                  {instagramFooterHint(igStep)}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              {currentStep === STEP_ACCOUNTS ? (
                <Button variant="outline" onClick={onClose} disabled={saving}>
                  Cancel
                </Button>
              ) : (
                // Frozen while a grant is starting: its draft is already parked,
                // so any move now would be undone by the resume — the user would
                // come back to the decision they had at the click, not this one.
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={saving || granting || grantingInstagram}
                >
                  Back
                </Button>
              )}

              {currentStep === STEP_PIXELS ? (
                <>
                  <Button variant="outline" onClick={handleSkipPixels} disabled={saving}>
                    Skip
                  </Button>
                  <Button onClick={handleConfirm} disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 className="size-4 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      'Connect'
                    )}
                  </Button>
                </>
              ) : currentStep === STEP_PAGES ? (
                <>
                  {/* Always available: it is what makes the step optional, and
                      it sends no page key at all. */}
                  <Button variant="outline" onClick={handleSkipPages} disabled={saving || granting}>
                    Skip
                  </Button>
                  <Button onClick={handleNext} disabled={!canNext() || saving || granting}>
                    Next
                  </Button>
                </>
              ) : currentStep === STEP_INSTAGRAM ? (
                <>
                  {/* The Pages footer again: Skip is always available and sends
                      no Instagram key at all — including while the listing
                      loads, after it failed, and without the permission. */}
                  <Button
                    variant="outline"
                    onClick={handleSkipInstagram}
                    disabled={saving || grantingInstagram}
                  >
                    Skip
                  </Button>
                  <Button onClick={handleNext} disabled={!canNext() || saving || grantingInstagram}>
                    Next
                  </Button>
                </>
              ) : (
                <Button onClick={handleNext} disabled={!canNext() || saving}>
                  Next
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
